import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/components/ui';
import { useHierarchyPage } from './Hierarchy/useHierarchyPage';
import { HierarchyTreePanel } from './Hierarchy/HierarchyTreePanel';
import { HierarchyDetailPanel } from './Hierarchy/HierarchyDetailPanel';
import { OrgCreateModal } from './Hierarchy/OrgCreateModal';
import { MoveOrgDialog } from './Hierarchy/MoveOrgDialog';
import { MoveOrganizationModal } from '@/components/organizations/MoveOrganizationModal';
import { OrgMoveConfirmDialog } from './Hierarchy/OrgMoveConfirmDialog';
import { OrgDeactivateDialog } from './Hierarchy/OrgDeactivateDialog';
import { OrgRetypeDialog } from './Hierarchy/OrgRetypeDialog';
import { LevelAssignmentModals } from './Hierarchy/LevelAssignmentModals';

/**
 * EAA-1818: `/console/hierarchy` — the design's nested đơn vị tree, now wired to
 * the real `Organization.parent` hierarchy.
 *
 * The tree is built from `Organization` rows and loads one level per expansion;
 * the org's Levels stay in a group BELOW it, because a Level belongs to the
 * organization rather than to any one đơn vị. See `hierarchy/types.ts` for the
 * data-model note — including why `OrgTenant`/`OrganizationAppScope` was the
 * wrong entity for this screen and what EAA-1836 changed — and
 * `hierarchy/useOrgTree.ts` for the lazy-loading model and the acting-org-as-root
 * decision.
 *
 * EAA-1818: frame 29's redesign made the tree the ONLY view — the "Cây | Bảng"
 * toggle and the flat tables it switched to are gone (`HierarchyTableView.tsx`
 * removed with them). The "Tình trạng cấp phát" summary and the org name/code
 * card are gone too: the org's name now lives in the page subtitle, and the
 * coverage stats' role moved to the org-detail screen in the new design.
 *
 * EAA-1838: the tier-ladder panel is GONE, and the divergence note that used to
 * defend its place here went with it. The argument was that portal-api shipped
 * OrgTier CRUD, so the FE had to expose a page-level authoring surface or the
 * endpoints would be orphaned — true of the old model, and the reason the guide
 * and the implementation knowingly disagreed. The vocabulary is now authored
 * inline from the create modal's picker (`UnitTypePicker`), so the endpoints stay
 * reachable with no extra toolbar control, and this screen matches
 * `ORG-HIERARCHY-GUIDE.md` §29's component list again. `LevelAssignmentModals`
 * still diverges and still stays — see its own note.
 *
 * That removal IS the fix for the reported "không biết thêm ở đâu": creating a
 * loại đơn vị no longer requires discovering a toolbar button labelled like a
 * filter, opening a panel, and finding a primary action in its footer.
 *
 * All state, queries, mutations and drag/keyboard logic live in
 * `useHierarchyPage()`; this file only lays out the shell.
 *
 * GATING — two capabilities, not one (see `useHierarchyPage`'s permission note):
 * reads need `console.orgs.view`; renaming AND assigning a đơn vị's cấp bậc need
 * `org:{actingOrgId}.manage`; creating a child đơn vị, moving one, ngưng hoạt động
 * and authoring the cấp bậc ladder need `console.orgs.manage`, which portal-api
 * issues to super-admins only — the same gates those endpoints enforce server-side.
 *
 * EAA-1838: the "Cấp bậc" button sits in the toolbar rather than in the detail
 * aside or a row menu, because the ladder is per ROOT ORGANIZATION
 * (`/organizations/{rootOrgId}/tiers`) while both of those surfaces are per node.
 * See `TierLadderPanel` for the full placement rationale.
 */
export function HierarchyPage() {
    const { t } = useTranslation();
    const vm = useHierarchyPage();
    const { org, rootOrg, canManage, canManageTree, canView } = vm;

    // §1b render gate mirrors the pre-rebuild page: without the read
    // capability this renders nothing (queries inside the hook never ran
    // either — `enabled` already guards on the same capability).
    if (!canView) {
        return null;
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit hz-page">
            {/* EAA-1818: composition per ORG-HIERARCHY-GUIDE.md §29, which states the
                screen was "rebuilt this pass to match Organizations' page composition —
                H1 + subtitle in the page flow, one search/filter row, the tree as a
                plain white card, no duplicate inner toolbar". The single-card layout
                this replaces read the in-shell frames as one surface; frame 09 (the one
                the guide marks as rebuilt) shows the head and filter row OUTSIDE the
                card instead. Frames 30-33 still draw the old inner toolbar in their
                backgrounds — they were not re-rendered, so 09 is authoritative. */}
            <div className="hz-page-head">
                <div className="hz-page-head-text">
                    <h1 className="hz-page-title">{t('console.hierarchy.page.title')}</h1>
                    {/* The subtitle carries the counts AND the non-inheritance rule,
                        which §29 requires stated "up front" rather than only in the
                        aside. `unitCount` counts LOADED descendants only — the tree
                        fetches one level per expansion — so `unitCountPartial` adds a
                        "+" rather than implying the figure is a total. */}
                    {org && (
                        <p className="hz-page-subtitle">
                            {t('console.hierarchy.page.subtitle', {
                                name: org.name,
                                count: vm.unitCount,
                                plus: vm.unitCountPartial ? '+' : '',
                                levelCount: vm.levels.length,
                            })}
                        </p>
                    )}
                    {org && !canManage && (
                        <p className="portal-admin-hint">{t('console.hierarchy.page.viewOnlyHint')}</p>
                    )}
                </div>

                {/* EAA-1818: secondary toolbar actions, ordered so "+ Đơn vị" stays last —
                    it is the primary action and the design keeps it at the right edge. */}
                {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">` —
                    see AccountDetailModal.tsx for the mapping rationale (props-for-classes, same
                    antd theme tokens the portal-btn CSS hand-implemented). */}
                <div className="hz-page-head-actions">
                    {canManage && (
                        <Button onClick={() => vm.setLevelModalOpen(true)}>
                            {t('console.hierarchy.page.addLevelButton')}
                        </Button>
                    )}
                    {canManage && (
                        <Button onClick={() => vm.setAssignModalOpen(true)}>
                            {t('console.hierarchy.page.assignButton')}
                        </Button>
                    )}
                    {/* EAA-1818: creating an organization is POST /organizations, super-admin
                        only — so this is gated on canManageTree, not canManage. The new đơn
                        vị lands under the tree's root. */}
                    {rootOrg && canManageTree && vm.tree.length > 0 && (
                        <Button type="primary" onClick={() => vm.openCreateChild(vm.tree[0])}>
                            {t('console.hierarchy.page.addOrgButton')}
                        </Button>
                    )}
                </div>
            </div>

            {!org && <p className="portal-admin-empty">{t('console.hierarchy.page.noActiveOrg')}</p>}

            {org && (
                <>
                    {/* §29's "one search/filter row": search on the left, collapse on the
                        right, both outside the tree card. */}
                    <div className="hz-filter-row">
                        {/* EAA-1804: raw <input> with a hand-drawn magnifier → kit Input, icon
                            moved into its `prefix` slot — same shape as AppsToolbar's
                            already-converted search box. */}
                        <Input
                            className="hz-search"
                            type="text"
                            placeholder={t('console.hierarchy.page.searchPlaceholder')}
                            value={vm.searchQuery}
                            onChange={(e) => vm.setSearchQuery(e.target.value)}
                            aria-label={t('console.hierarchy.page.searchAriaLabel')}
                            prefix={
                                <svg
                                    viewBox="0 0 24 24"
                                    width="15"
                                    height="15"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.8}
                                    aria-hidden="true"
                                >
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                                </svg>
                            }
                        />
                        <button
                            type="button"
                            className="hz-collapse-btn"
                            onClick={vm.collapseAll}
                            disabled={vm.expandedIds.size === 0}
                            // EAA-1818: collapse only. "Expand all" over a lazily loaded
                            // tree means one request per node at unknown depth, so it is
                            // not offered — the frame shows a single button too.
                            title={t('console.hierarchy.page.collapseAllTitle')}
                        >
                            {t('console.hierarchy.page.collapseAllButton')}
                        </button>
                    </div>

                    {/* EAA-1804: `.hz-columns` is this page's ONE fit-grow region. Before
                        this the page had no `--fit` at all, so `.hz-shell`'s hardcoded
                        `max-height: 42rem` was the only thing bounding the tree — a fixed
                        cap unrelated to the viewport, sitting inside a page that still grew
                        and scrolled on the shell. That is the two-scrollbar failure the
                        fit contract exists to prevent. */}
                    <div className="hz-columns fit-grow">
                        <HierarchyTreePanel vm={vm} />
                        <HierarchyDetailPanel vm={vm} />
                    </div>
                </>
            )}

            <OrgCreateModal vm={vm} />
            <MoveOrgDialog vm={vm} />
            {/* EAA-1891: cross-tree adoption — the SAME component /organizations
                uses, so the two entry points cannot drift. Distinct from
                MoveOrgDialog above: that one re-parents inside this tree. */}
            <MoveOrganizationModal
                open={vm.adoptDialogOrg !== null}
                organization={vm.adoptDialogOrg}
                onClose={vm.closeAdoptDialog}
            />
            <OrgMoveConfirmDialog vm={vm} />
            <OrgDeactivateDialog vm={vm} />
            <OrgRetypeDialog vm={vm} />
            <LevelAssignmentModals vm={vm} />
        </main>
    );
}

export default HierarchyPage;
